import i18n, { InitOptions, Resource } from 'i18next';
import 'moment/min/locales.min'; // import all moment locals

const supportedLanguages = ['en', 'de'];

// Function to handle errors so we don't push with wrong translation
const missingKeyHandler = (
  lngs: Readonly<string[]>,
  ns: string,
  key: string,
  fallbackValue: string,
) => {
  console.warn(
    `i18next: The key '${key}' does not exist in the namespace '${ns}' for language '${i18n.language}'. Default to ${fallbackValue}`,
  );
};

// for all config options see: https://www.i18next.com/overview/configuration-options
export const initializeI18n = async (translations: Resource) => {
  const resources: any = translations['default'] ?? translations;
  const i18nextOptions: InitOptions = {
    // the json loader creates a default object
    resources: resources,
    fallbackLng: supportedLanguages[0],
    interpolation: {
      escapeValue: false,
    },
    saveMissing: true,
    missingKeyHandler,
  };
  // Language is set by language content provider
  await i18n.init(i18nextOptions);
  return true;
};

export const getDateFormatOptions = (
  includeTime: boolean = false,
  language: string = i18n.language,
  excludeDay: boolean = false,
  includeSeconds = false,
): Intl.DateTimeFormatOptions => {
  let options: Intl.DateTimeFormatOptions = {};

  switch (language) {
    case 'en': {
      options.year = 'numeric';
      options.month = 'long';
      options.day = excludeDay ? undefined : 'numeric';
      break;
    }

    default: {
      options.year = 'numeric';
      options.month = '2-digit';
      options.day = excludeDay ? undefined : '2-digit';
      break;
    }
  }

  if (includeTime) {
    options.hour = 'numeric';
    options.minute = 'numeric';
    options.second = includeSeconds ? 'numeric' : undefined;
    options.timeZoneName = 'short';
  }

  return options;
};

export const DateFormatOptionsForExport: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  timeZoneName: 'short',

  timeZone: 'Europe/Berlin',
};

export const DateFormatOptionsForFileNames: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',

  timeZone: 'Europe/Berlin',
};

export const formatStreet = (streetName: string, streetNumber: string) => {
  let formatted = `${streetNumber} ${streetName}`;
  switch (i18n.language) {
    case 'de': {
      formatted = `${streetName} ${streetNumber}`;
      break;
    }
    default: {
      break;
    }
  }
  return formatted;
};

export const formatNumber = (
  value: number,
  language: string = i18n.language,
): string => {
  const formatter = new Intl.NumberFormat(language, {
    style: 'decimal',
  });

  return formatter.format(value);
};

export default i18n;
